import {
	ShipmentLocationActionDtoLocationActionType,
	type ShipmentLocationActionRequestDto,
	ShipmentLocationActionRequestDtoLocationActionType,
} from '@uturn/api/v1';
import { TransportTypeCode } from '@uturn/portal/types/shipment';
import type { Item } from '../components/multi-select-editor';

export const matchLocationField = (colId: string, property: string) => {
	const regex = new RegExp(`location_[0-9]*_${property}`);
	return !!regex.exec(colId);
};

const ACTION_TYPE_BY_BULK_VALUE: Record<
	string,
	ShipmentLocationActionRequestDtoLocationActionType
> = {
	'pick-up': ShipmentLocationActionRequestDtoLocationActionType.PICKUP,
	deliver: ShipmentLocationActionRequestDtoLocationActionType.DELIVER,
	load: ShipmentLocationActionRequestDtoLocationActionType.LOAD,
	drop: ShipmentLocationActionRequestDtoLocationActionType.DROP,
	stop: ShipmentLocationActionRequestDtoLocationActionType.STOP,
};

// Builds the DTO array consumed by `validateLocationDates` from the flat AG Grid
// row shape (`location_<n>_from`, `location_<n>_until`, `location_<n>_type`).
// Missing dates / actionType are tolerated by the validator.
export const toLocationActionsFromRow = (
	row: Record<string, { value?: unknown } | undefined>,
): ShipmentLocationActionRequestDto[] => {
	const locationIndices = Object.keys(row)
		.map((columnId) => columnId.match(/^location_(\d+)_from$/))
		.filter((match): match is RegExpMatchArray => match !== null)
		.map((match) => Number(match[1]));

	if (locationIndices.length === 0) {
		return [];
	}

	const maxLocationIndex = Math.max(...locationIndices);

	return Array.from({ length: maxLocationIndex }, (_, sequenceNumber) => {
		const locationNumber = sequenceNumber + 1;
		const typeCell = row[`location_${locationNumber}_type`]?.value as
			| { value?: string }
			| undefined;
		const fromDate = row[`location_${locationNumber}_from`]?.value;
		const untilDate = row[`location_${locationNumber}_until`]?.value;

		return {
			sequenceNumber,
			locationActionType:
				ACTION_TYPE_BY_BULK_VALUE[typeCell?.value ?? ''] ??
				ShipmentLocationActionRequestDtoLocationActionType.PICKUP,
			location: {},
			dateFrom: fromDate instanceof Date ? fromDate.toISOString() : undefined,
			dateUntil:
				untilDate instanceof Date ? untilDate.toISOString() : undefined,
		};
	});
};

export const getInitialLocations = (
	transportType: TransportTypeCode,
): ShipmentLocationActionDtoLocationActionType[] => {
	if (transportType === TransportTypeCode.IMPORT) {
		return [
			ShipmentLocationActionDtoLocationActionType['Pick-up'],
			ShipmentLocationActionDtoLocationActionType.Deliver,
			ShipmentLocationActionDtoLocationActionType.Drop,
		];
	}

	if (transportType === TransportTypeCode.EXPORT) {
		return [
			ShipmentLocationActionDtoLocationActionType['Pick-up'],
			ShipmentLocationActionDtoLocationActionType.Load,
			ShipmentLocationActionDtoLocationActionType.Drop,
		];
	}

	if (transportType === TransportTypeCode.SHUNT) {
		return [
			ShipmentLocationActionDtoLocationActionType['Pick-up'],
			ShipmentLocationActionDtoLocationActionType.Drop,
		];
	}

	return [];
};

export const getDefaultLocationData = (
	transportType: TransportTypeCode,
): Record<string, Item> => {
	const locations = getInitialLocations(transportType);

	return locations.reduce(
		(acc, location, index) => ({
			...acc,
			[`location_${index + 1}_type`]: {
				value: {
					value: location.toLowerCase(),
					label: location,
				},
			},
		}),
		{},
	);
};
